'use client';

import { endOfMonth, format, isSameDay, isSameMonth, startOfDay, startOfMonth, subDays, subMonths } from 'date-fns';
import { CalendarDays, Check, ChevronDown } from 'lucide-react';
import { useId, useMemo, useState } from 'react';
import type { DateRange, Matcher } from 'react-day-picker';

import { useDynamicLocale } from '@/hoslog-intl';
import { cn } from '@/lib/utils';
import { Button } from 'ui/button';
import { Calendar } from 'ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from 'ui/popover';

export type DateRangePreset = 'today' | 'last7Days' | 'last30Days' | 'thisMonth' | 'lastMonth';
type PresetId = DateRangePreset | 'custom';

/** Inclusive range the selection must stay within. */
export type DateRangeBounds = { from: Date; to: Date };

interface DateRangePickerProps {
  value?: DateRange;
  onRangeSelect: (dateRange: DateRange | undefined) => void;
  onPresetSelect?: (dateRange: DateRange) => void;
  allowFutureDates?: boolean;
  defaultPreset?: DateRangePreset;
  showDefaultRangeWhenEmpty?: boolean;
  className?: string;
  hideChevron?: boolean;
  iconClassName?: string;
  /** Limits selection to these days; presets are hidden and an empty value shows the whole range. */
  bounds?: DateRangeBounds;
}

const presets: { id: PresetId; label: { en: string; ja: string } }[] = [
  { id: 'today', label: { en: 'Today', ja: '今日' } },
  { id: 'last7Days', label: { en: 'Last 7 days', ja: '過去7日間' } },
  { id: 'last30Days', label: { en: 'Last 30 days', ja: '過去30日間' } },
  { id: 'thisMonth', label: { en: 'This month', ja: '今月' } },
  { id: 'lastMonth', label: { en: 'Last month', ja: '先月' } },
  { id: 'custom', label: { en: 'Custom range', ja: '期間指定' } },
];

const yyyyMDDFormat = (date: Date) => format(date, 'y/MM/dd');
const japaneseWeekdays = ['日', '月', '火', '水', '木', '金', '土'];

export function getPresetRange(preset: DateRangePreset, today: Date): DateRange {
  switch (preset) {
    case 'today':
      return { from: today, to: today };
    case 'last7Days':
      return { from: subDays(today, 6), to: today };
    case 'last30Days':
      return { from: subDays(today, 29), to: today };
    case 'thisMonth':
      return { from: startOfMonth(today), to: today };
    case 'lastMonth': {
      const lastMonth = subMonths(today, 1);
      return { from: startOfMonth(lastMonth), to: endOfMonth(lastMonth) };
    }
  }
}

function getMatchingPreset(range: DateRange, today: Date): PresetId {
  for (const preset of presets) {
    if (preset.id === 'custom') continue;
    const candidate = getPresetRange(preset.id, today);
    if (
      range.from &&
      range.to &&
      candidate.from &&
      candidate.to &&
      isSameDay(candidate.from, range.from) &&
      isSameDay(candidate.to, range.to)
    ) {
      return preset.id;
    }
  }

  return 'custom';
}

const DateRangePicker = ({
  value,
  onRangeSelect,
  onPresetSelect,
  allowFutureDates = false,
  defaultPreset = 'thisMonth',
  showDefaultRangeWhenEmpty = true,
  className,
  hideChevron = false,
  iconClassName,
  bounds,
}: DateRangePickerProps) => {
  const id = useId();
  const { locale } = useDynamicLocale();
  const language = locale === 'en' ? 'en' : 'ja';
  const calendarFormatters = {
    formatCaption: (date: Date) =>
      language === 'en' ? format(date, 'MMMM yyyy') : `${date.getMonth() + 1}月 ${date.getFullYear()}`,
    formatWeekdayName: (date: Date) => (language === 'en' ? format(date, 'EEEEEE') : japaneseWeekdays[date.getDay()]),
  };
  const today = useMemo(() => startOfDay(new Date()), []);
  const boundsFrom = bounds ? startOfDay(bounds.from).getTime() : undefined;
  const boundsTo = bounds ? startOfDay(bounds.to).getTime() : undefined;
  const boundedRange = useMemo<DateRange | undefined>(
    () =>
      boundsFrom !== undefined && boundsTo !== undefined
        ? { from: new Date(boundsFrom), to: new Date(boundsTo) }
        : undefined,
    [boundsFrom, boundsTo],
  );
  const defaultRange = useMemo<DateRange>(
    () => boundedRange ?? getPresetRange(defaultPreset, today),
    [boundedRange, defaultPreset, today],
  );
  const hasExplicitRange = Boolean(value?.from || value?.to);
  const showEmptyState = !hasExplicitRange && !showDefaultRangeWhenEmpty && !boundedRange;
  const displayedRange = value ?? defaultRange;
  const selectedMonth = startOfMonth(displayedRange.from ?? today);
  const previousSelectedMonth = boundedRange ? selectedMonth : subMonths(selectedMonth, 1);
  // Presets ("Today", "Last month", ...) can fall outside the bounds, so a bounded picker only offers the calendar.
  const visiblePresets = boundedRange ? [] : presets;
  const getDisplayedPreset = (range: DateRange): PresetId =>
    boundedRange ? 'custom' : getMatchingPreset(range, today);
  const displayedPreset = showEmptyState ? null : getDisplayedPreset(displayedRange);
  const disabledDays: Matcher[] = [
    ...(allowFutureDates ? [] : [{ after: today }]),
    ...(boundedRange?.from && boundedRange.to ? [{ before: boundedRange.from }, { after: boundedRange.to }] : []),
  ];
  const hasShortLabel = showEmptyState || displayedPreset === 'today';
  const [open, setOpen] = useState(false);
  const [activePreset, setActivePreset] = useState<PresetId | null>(() =>
    showEmptyState ? null : getDisplayedPreset(displayedRange),
  );
  const [month, setMonth] = useState(startOfMonth(displayedRange.from ?? today));
  const [draftRange, setDraftRange] = useState<DateRange>(displayedRange);
  const [selectionPhase, setSelectionPhase] = useState<'from' | 'to'>('from');
  const presetMonthCount =
    displayedRange.from && displayedRange.to && !isSameMonth(displayedRange.from, displayedRange.to) ? 2 : 1;

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setDraftRange(displayedRange);
      setSelectionPhase('from');
      const matchingPreset = showEmptyState ? null : getDisplayedPreset(displayedRange);
      setActivePreset(matchingPreset);
      setMonth(matchingPreset === 'custom' ? previousSelectedMonth : startOfMonth(displayedRange.from ?? today));
    }
    setOpen(nextOpen);
  };

  const selectPreset = (preset: PresetId) => {
    setActivePreset(preset);
    if (preset === 'custom') {
      setDraftRange(displayedRange);
      setSelectionPhase('from');
      setMonth(previousSelectedMonth);
      return;
    }

    const nextRange = getPresetRange(preset, today);
    setMonth(startOfMonth(nextRange.from ?? today));
    onPresetSelect?.(nextRange);
    if (!onPresetSelect) onRangeSelect(nextRange);
    setOpen(false);
  };

  const selectCustomDate = (date: Date) => {
    if (selectionPhase === 'from') {
      setDraftRange({ from: date, to: undefined });
      setSelectionPhase('to');
      return;
    }

    const from = draftRange.from ?? date;
    const nextRange = date < from ? { from: date, to: from } : { from, to: date };

    setDraftRange(nextRange);
    onRangeSelect(nextRange);
    setSelectionPhase('from');
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          variant="outline"
          className={cn(
            'group justify-between bg-card px-3 font-medium outline-offset-0 hover:bg-card focus-visible:border-ring focus-visible:outline-[3px] focus-visible:outline-ring/20',
            !hasShortLabel && 'min-w-[230px]',
            className,
          )}
        >
          <span className="flex min-w-0 items-center gap-2">
            <CalendarDays className={cn('shrink-0 text-muted-foreground', iconClassName)} aria-hidden="true" />
            <span className="truncate">
              {showEmptyState ? (
                language === 'en' ? (
                  'All dates'
                ) : (
                  'すべての日付'
                )
              ) : displayedPreset === 'today' ? (
                presets.find((preset) => preset.id === 'today')?.label[language]
              ) : (
                <>
                  {yyyyMDDFormat(displayedRange.from ?? defaultRange.from!)} ~{' '}
                  {yyyyMDDFormat(displayedRange.to ?? defaultRange.to!)}
                </>
              )}
            </span>
          </span>
          {!hideChevron && (
            <ChevronDown
              className="shrink-0 text-muted-foreground/80 transition-colors group-hover:text-foreground"
              aria-hidden="true"
            />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto max-w-[calc(100vw-3rem)] overflow-x-auto p-0" align="end" collisionPadding={24}>
        <div className="flex min-w-max">
          {visiblePresets.length > 0 && (
            <div className="w-40 shrink-0 border-r border-border p-2">
              {visiblePresets.map((preset) => (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => selectPreset(preset.id)}
                  className={cn(
                    'flex h-9 w-full cursor-pointer items-center gap-2 rounded-md px-2 text-left text-sm transition-colors hover:bg-surface-hover',
                    activePreset === preset.id && 'bg-surface-hover font-medium ring-1 ring-primary/20',
                  )}
                >
                  <span className="flex size-4 shrink-0 items-center justify-center">
                    {activePreset === preset.id && <Check aria-hidden="true" />}
                  </span>
                  {preset.label[language]}
                </button>
              ))}
            </div>
          )}

          {activePreset === 'custom' ? (
            <div className="p-3">
              <p className="mb-1 px-2 text-xs font-medium text-muted-foreground">
                {language === 'en'
                  ? `Select ${selectionPhase === 'from' ? 'start' : 'end'} date`
                  : selectionPhase === 'from'
                    ? '開始日を選択'
                    : '終了日を選択'}
              </p>
              <Calendar
                mode="range"
                weekStartsOn={language === 'en' ? 0 : 1}
                formatters={calendarFormatters}
                month={month}
                onMonthChange={setMonth}
                numberOfMonths={boundedRange ? 1 : 2}
                startMonth={boundedRange?.from}
                endMonth={boundedRange?.to}
                selected={draftRange}
                onDayClick={selectCustomDate}
                disabled={disabledDays.length > 0 ? disabledDays : undefined}
                excludeDisabled
                fixedWeeks
              />
            </div>
          ) : (
            <div className="p-3">
              <Calendar
                mode="range"
                weekStartsOn={language === 'en' ? 0 : 1}
                formatters={calendarFormatters}
                month={month}
                onMonthChange={setMonth}
                numberOfMonths={presetMonthCount}
                startMonth={boundedRange?.from}
                endMonth={boundedRange?.to}
                selected={showEmptyState ? undefined : displayedRange}
                disabled={disabledDays.length > 0 ? disabledDays : undefined}
                fixedWeeks
              />
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export { DateRangePicker };
